import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { ShieldAlert, Plus, Pencil, Trash2, X } from "lucide-react";

const EMPTY = {
  paese: "", regione: "", ambito: "nazionale", categoria: "conflitto_armato", livello: "significativa",
  profili: "", descrizione: "", fonte: "", url: "", periodo_riferimento: "",
  data_pubblicazione: "", data_ultimo_controllo: "", data_revisione: "", lat: null, lon: null,
};

export default function COIManager() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const { data: meta } = useQuery({ queryKey: ["coi-meta"], queryFn: async () => (await api.get("/coi/categories")).data });
  const { data } = useQuery({ queryKey: ["coi-indicators"], queryFn: async () => (await api.get("/coi/indicators")).data });
  const cats = meta?.categories || [];
  const catLabel = (k) => cats.find((c) => c.key === k)?.label || k;
  const indicators = data?.indicators || [];

  const startNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const startEdit = (i) => { setForm({ ...EMPTY, ...i }); setEditId(i.id); setOpen(true); };

  const save = async () => {
    if (!form.paese.trim()) return toast.error("Il Paese è obbligatorio");
    setSaving(true);
    try {
      const payload = { ...form, lat: form.lat === "" ? null : form.lat, lon: form.lon === "" ? null : form.lon };
      if (editId) await api.put(`/coi/indicators/${editId}`, payload);
      else await api.post("/coi/indicators", payload);
      toast.success(editId ? "Indicatore aggiornato" : "Indicatore aggiunto");
      qc.invalidateQueries({ queryKey: ["coi-indicators"] });
      qc.invalidateQueries({ queryKey: ["coi-map"] });
      setOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore salvataggio"); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare questo indicatore COI?")) return;
    await api.delete(`/coi/indicators/${id}`);
    qc.invalidateQueries({ queryKey: ["coi-indicators"] });
    qc.invalidateQueries({ queryKey: ["coi-map"] });
    toast.success("Indicatore eliminato");
  };

  const inp = "w-full mt-1 px-2.5 py-1.5 text-sm bg-white border border-[#1A2B4A]/20 rounded-sm focus:outline-none focus:border-[#C9A84C]";

  return (
    <div className="border border-[#1A2B4A]/12 rounded-sm p-5 mt-6" data-testid="coi-manager">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-serif text-xl text-[#1A2B4A] flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-[#C9A84C]" /> Rilevanza COI</h3>
        <button data-testid="coi-add-btn" onClick={startNew} className="inline-flex items-center gap-1.5 text-xs bg-[#1A2B4A] text-white px-3 py-1.5 rounded-md hover:bg-[#22345c] transition-colors"><Plus className="w-3.5 h-3.5" /> Nuovo indicatore</button>
      </div>
      <p className="text-xs text-[#4a5568] mb-4">Dati Country of Origin Information amministrabili, sovrapposti alla mappa. Usa fonti istituzionali (EUAA, UNHCR/Refworld, OHCHR). Aggiorna la data di revisione: gli indicatori scaduti vengono mostrati come non aggiornati.</p>

      {indicators.length === 0 ? (
        <p className="text-sm text-[#4a5568]">Nessun indicatore COI.</p>
      ) : (
        <div className="space-y-2">
          {indicators.map((i) => (
            <div key={i.id} data-testid={`coi-row-${i.id}`} className="flex items-start justify-between gap-3 border border-[#1A2B4A]/10 rounded-lg px-3 py-2">
              <div className="min-w-0">
                <div className="text-sm font-medium text-[#1A2B4A] truncate">
                  {i.paese}{i.regione ? ` · ${i.regione}` : ""}
                  {i.scaduto && <span className="ml-2 text-[10px] bg-[#faf5e6] text-[#8a6d1f] px-1.5 py-0.5 rounded-md">non aggiornato</span>}
                </div>
                <div className="text-xs text-[#4a5568] truncate">{catLabel(i.categoria)} · {i.livello} · {i.ambito}</div>
                <div className="text-[11px] text-[#4a5568] truncate">{i.fonte}{i.data_ultimo_controllo ? ` · ctrl ${i.data_ultimo_controllo}` : ""}</div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button data-testid={`coi-edit-${i.id}`} onClick={() => startEdit(i)} className="p-1.5 rounded-md text-[#1A2B4A] hover:bg-[#f0ede4]"><Pencil className="w-4 h-4" /></button>
                <button data-testid={`coi-del-${i.id}`} onClick={() => del(i.id)} className="p-1.5 rounded-md text-[#9b2c2c] hover:bg-[#faeaea]"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-[#16233c]/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="coi-form-dialog">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-4 border-t-2 border-[#C9A84C] shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-xl text-[#1A2B4A]">{editId ? "Modifica indicatore COI" : "Nuovo indicatore COI"}</h3>
              <button onClick={() => setOpen(false)} className="text-[#4a5568] hover:text-[#1A2B4A]"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-x-4 gap-y-3">
              <div><label className="text-xs text-[#4a5568]">Paese *</label><input data-testid="coi-f-paese" value={form.paese} onChange={(e) => setForm({ ...form, paese: e.target.value })} className={inp} /></div>
              <div><label className="text-xs text-[#4a5568]">Regione</label><input data-testid="coi-f-regione" value={form.regione} onChange={(e) => setForm({ ...form, regione: e.target.value })} className={inp} /></div>
              <div><label className="text-xs text-[#4a5568]">Categoria</label>
                <select data-testid="coi-f-categoria" value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })} className={inp}>
                  {cats.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select></div>
              <div><label className="text-xs text-[#4a5568]">Livello di rilevanza</label>
                <select data-testid="coi-f-livello" value={form.livello} onChange={(e) => setForm({ ...form, livello: e.target.value })} className={inp}>
                  <option value="elevata">Rilevanza COI elevata</option>
                  <option value="significativa">Rilevanza COI significativa</option>
                  <option value="approfondimento">Approfondimento necessario</option>
                  <option value="insufficiente">Dati insufficienti o non aggiornati</option>
                </select></div>
              <div><label className="text-xs text-[#4a5568]">Ambito</label>
                <select data-testid="coi-f-ambito" value={form.ambito} onChange={(e) => setForm({ ...form, ambito: e.target.value })} className={inp}>
                  <option value="nazionale">Rischio generalizzato nazionale</option>
                  <option value="regionale">Rischio limitato a una regione</option>
                  <option value="profilo">Rischio collegato a profili personali</option>
                  <option value="contestuale">Informazione contestuale</option>
                </select></div>
              <div><label className="text-xs text-[#4a5568]">Periodo di riferimento</label><input data-testid="coi-f-periodo" value={form.periodo_riferimento} onChange={(e) => setForm({ ...form, periodo_riferimento: e.target.value })} placeholder="es. 2023-2024" className={inp} /></div>
              <div className="sm:col-span-2"><label className="text-xs text-[#4a5568]">Profili potenzialmente esposti</label><input data-testid="coi-f-profili" value={form.profili} onChange={(e) => setForm({ ...form, profili: e.target.value })} className={inp} /></div>
              <div className="sm:col-span-2"><label className="text-xs text-[#4a5568]">Descrizione</label><textarea data-testid="coi-f-descrizione" rows={2} value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} className={inp + " resize-none"} /></div>
              <div><label className="text-xs text-[#4a5568]">Fonte</label><input data-testid="coi-f-fonte" value={form.fonte} onChange={(e) => setForm({ ...form, fonte: e.target.value })} placeholder="es. EUAA Country Guidance" className={inp} /></div>
              <div><label className="text-xs text-[#4a5568]">URL fonte</label><input data-testid="coi-f-url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className={inp} /></div>
              <div><label className="text-xs text-[#4a5568]">Data pubblicazione</label><input data-testid="coi-f-pub" type="date" value={form.data_pubblicazione} onChange={(e) => setForm({ ...form, data_pubblicazione: e.target.value })} className={inp} /></div>
              <div><label className="text-xs text-[#4a5568]">Data ultimo controllo</label><input data-testid="coi-f-ctrl" type="date" value={form.data_ultimo_controllo} onChange={(e) => setForm({ ...form, data_ultimo_controllo: e.target.value })} className={inp} /></div>
              <div><label className="text-xs text-[#4a5568]">Data revisione prevista</label><input data-testid="coi-f-rev" type="date" value={form.data_revisione} onChange={(e) => setForm({ ...form, data_revisione: e.target.value })} className={inp} /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><label className="text-xs text-[#4a5568]">Lat (opz.)</label><input value={form.lat ?? ""} onChange={(e) => setForm({ ...form, lat: e.target.value === "" ? null : parseFloat(e.target.value) })} className={inp} /></div>
                <div><label className="text-xs text-[#4a5568]">Lon (opz.)</label><input value={form.lon ?? ""} onChange={(e) => setForm({ ...form, lon: e.target.value === "" ? null : parseFloat(e.target.value) })} className={inp} /></div>
              </div>
            </div>
            <p className="text-[11px] text-[#4a5568] mt-3">Se lat/lon sono vuoti, le coordinate vengono ricavate automaticamente da Paese/Regione.</p>
            <div className="flex justify-end gap-3 mt-5">
              <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm border border-[#1A2B4A] rounded-sm hover:bg-[#f9f8f5]">Annulla</button>
              <button data-testid="coi-save-btn" onClick={save} disabled={saving} className="px-4 py-2 text-sm bg-[#1A2B4A] text-white rounded-sm hover:bg-[#22345c] disabled:opacity-50">{saving ? "Salvo…" : "Salva"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
